import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import type { AppEpic } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { of } from 'rxjs';
import { inputIsNotNullOrUndefined } from 'src/utils/Functions/epicsFunctions';
import {
  mfpModuleReady,
  setActivePage,
  setActivePerspective,
  setActiveTab,
  setRedirectOnScopeExpired,
} from './NavigationShell.slice';
import { computeTabPath, getMfpModule } from './navigationUtils';
import { updateFilterSensitivity } from 'src/components/FilterPanel/FilterPanel.slice';
import type { AppThunk } from 'src/store.types';
import { concatMap, take, tap } from 'rxjs/operators';
import { appReady } from 'src/state/ViewConfig/ViewConfig.slice';
import { Observable } from 'rxjs/internal/Observable';
import { getSettings } from 'src/state/settings/Settings.actions';
import { checkIfHasMfpModule } from 'src/components/higherOrder/MfpScopeSensitive';
import { getAvailableScopeMembers } from 'src/state/scope/Scope.actions';
import { scopeExpired } from 'src/state/scope/Scope.slice';
import { receivedWorkingSets } from 'src/state/workingSets/workingSets.slice';
import { getScopeId } from 'src/state/scope/Scope.types';

const NON_FILTERABLE_COMPONENTS = [
  'AssortmentCart',
  'ParameterToggles',
  'GeoTrends',
  'MacroMix',
  'TopPerformers',
  'TargetList',
  'Summary',
  // TODO add to list if necessary
];
/**
 * This epic is responsible for setting the page filter sensitivity on any page navigation.
 * It assumes all pages are filter sensitive except a small known set seen above
 *
 */
export const setActivePageFilterSensitivityEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    ofType(setActivePage.type),
    map(() => {
      const viewConf = state$.value.perspective.activePageConf;
      // if there's no view (or no component), fall back to filters=on:
      const component = viewConf?.component;
      const filtersOn = !component || !NON_FILTERABLE_COMPONENTS.includes(component);
      return updateFilterSensitivity(filtersOn);
    }),
  );

export const checkIfMfpModuleIsReady: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(appReady.type, setActivePerspective.type, setActiveTab.type),
    filter(() => checkIfHasMfpModule(state$.value.appConfig.tenantConfig)),
    concatMap((_incoming) => {
      return of(mfpModuleReady());
    }),
  );
};

const needsMfpSettings = (state: AppState): boolean => {
  return (
    checkIfHasMfpModule(state.appConfig.tenantConfig) ||
    state.appConfig.tenantConfig.perspective?.scopeConfig?.type === 'MfpScopeSelector'
  );
};

export const setupSettings: AppEpic = (action$, state$, deps): Observable<AppThunk> => {
  const settingsService = deps.settings;
  return action$.pipe(
    ofType(appReady.type, mfpModuleReady.type),
    filter(() => needsMfpSettings(state$.value)),
    concatMap((_incoming) => {
      return of(getSettings(settingsService));
    }),
  );
};

export const getMfpScopeMembers: AppEpic = (action$, state$, deps): Observable<AppThunk> => {
  const settingsService = deps.settings;
  return action$.pipe(
    ofType(appReady.type, mfpModuleReady.type),
    filter(() => needsMfpSettings(state$.value)),
    map(() => getMfpModule(state$.value)),
    filter(inputIsNotNullOrUndefined),
    concatMap((mfpModule) => of(getAvailableScopeMembers(mfpModule))),
  );
};

export const setLocalization: AppEpic = (action$, _state$, deps) => {
  const localizationService = deps.localization;
  return action$.pipe(
    ofType(getSettings.fulfilled.type),
    take(1),
    tap((action) => {
      // WARNING: here by side effects!
      localizationService.setCulture(action.payload.localization.baseLocale);
    }),
  );
};

export const navBackToTargetSettingOnScopeExpired: AppEpic = (action$, state$, deps) =>
  action$.pipe(
    ofType(receivedWorkingSets.type),
    filter(() => checkIfHasMfpModule(state$.value.appConfig.tenantConfig) && state$.value.scope.scopeLoading === false),
    filter((action) => {
      const currentScopeId = getScopeId(state$.value.mfpScope);
      if (!currentScopeId) return false;
      return !action.payload.some((ws: { id: string }) => ws.id === currentScopeId);
    }),
    mergeMap(() => {
      const state = state$.value;
      const perspective = state.perspective.selected;
      const activeTab = state.perspective.activeTab;
      if (!perspective || !activeTab) return of();

      const boundTenant = deps.configService.getBindings(perspective);
      const tab = boundTenant.boundTabs.find((t) => t.pathSlot === activeTab) ?? boundTenant.defaultBoundTab;
      if (!tab) return of();

      const targetPath = '/' + [boundTenant.perspective.pathSlot, computeTabPath(tab)].join('/');

      return of(setRedirectOnScopeExpired(targetPath), scopeExpired());
    }),
  );
